import React from "react";
import "./GradePage.css";
import { FOOTER_GROUPS, findBySlug, footerGroupOf } from "../Shared/Seo/routeSeo.ts";
import { Seo } from "../Shared/Seo/Seo.tsx";
import { BlueprintCorners } from "../Shared/Theme/Blueprint.tsx";
import { useTranslation } from "../Shared/i18n/useTranslation.ts";

/**
 * A grade hub (ADR 0062): the grade's h1 and intro, then tiles for the skills
 * that fit the grade, grouped by strand, then the FAQ.
 *
 * It replaced a generator. Each grade page used to be one worksheet with a
 * grade preset, which answered "3rd grade math worksheets" with one skill of a
 * dozen. Each preset now has a page of its own, and that page is a tile here.
 *
 * Everything is manifest data: the sections and blurbs come from the entry's
 * `skillSections`, and a tile's title is the target page's own h1, so renaming
 * a page renames its tiles. scripts/prerender.js writes the same markup into
 * the static HTML (`hubContent`) - change both - so every tile is a crawlable
 * link before the bundle loads.
 */
export function GradePage({ slug }: { slug: string }) {
    const entry = findBySlug(slug);
    const t = useTranslation();

    if (!entry?.skillSections) {
        return null;
    }

    const kicker = FOOTER_GROUPS.find((group) => group.key === footerGroupOf(entry))?.heading;

    return (
        <>
            <Seo entry={entry} />
            <div className="gradeHub">
                <header className="gradeHubHeader">
                    {/* The footer group's own heading ("By grade"), so the
                        kicker is manifest data like everything else here. */}
                    {kicker && <div className="kicker">{kicker}</div>}
                    <h1>{entry.h1}</h1>
                    {entry.intro?.map((paragraph, i) => (
                        <p key={i}>{paragraph}</p>
                    ))}
                </header>

                {entry.skillSections.map((section) => (
                    <section key={section.heading} className="gradeHubSection">
                        <h2>{section.heading}</h2>
                        <ul className="gradeHubTiles">
                            {section.skills.map((skill) => {
                                const target = findBySlug(skill.slug);
                                return (
                                    <li key={skill.slug}>
                                        <a className="gradeHubTile blueprint" href={skill.slug}>
                                            <BlueprintCorners/>
                                            <span className="gradeHubTileTitle">{target?.h1 ?? skill.slug}</span>
                                            <span className="gradeHubTileBlurb">{skill.blurb}</span>
                                        </a>
                                    </li>
                                );
                            })}
                        </ul>
                    </section>
                ))}

                {entry.faq && entry.faq.length > 0 && (
                    <section className="landingFaq gradeHubFaq">
                        <h2>{t('settings.frequentlyAskedQuestions')}</h2>
                        {entry.faq.map((item, i) => (
                            <div key={i} className="faqItem">
                                <h3>{item.q}</h3>
                                <p>{item.a}</p>
                            </div>
                        ))}
                    </section>
                )}
            </div>
        </>
    );
}

export default GradePage;
